import { Flex, FormItemsGroup, Input, Spacing, Text } from '@billy/design-system'
import { Label } from '@billy/design-system/src/components/Label'
import { ReactElement } from 'react'
import { Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormItemProps } from 'src/hooks'

import { CompanyInfo, CompanyInfoSchema } from '../../utils'
import { AddressingLineFormat } from '../AddressingLineSelector/types/frAddressingLinesFormats'
import { CompanyNameTooltipIcon } from '../CompanyNameTooltipIcon'
import { ConditionalFormGroup } from './components/ConditionalFormGroup'

export type EditableFieldGroups = 'accountHolderName' | 'address' | 'companyName'

export interface ManualCompanyFormProps {
  addressingLineFormat: AddressingLineFormat
  fields?: EditableFieldGroups[] | null
  formItem: <Name extends Path<CompanyInfo>>(
    props: Omit<FormItemProps<CompanyInfoSchema, Name>, 'error'>,
  ) => JSX.Element
}

export const ManualCompanyForm = ({
  addressingLineFormat,
  fields = null,
  formItem: FormItem,
}: ManualCompanyFormProps): ReactElement => {
  const { t } = useTranslation()

  const companyRegistrationLabel =
    addressingLineFormat === AddressingLineFormat.SIREN
      ? t('e_invoice.kyc.step.company_details.form.company_registration.label_siren')
      : t('e_invoice.kyc.step.company_details.form.company_registration.label_siret')

  return (
    <>
      <Flex flexDirection="column" gap={Spacing.S16}>
        <ConditionalFormGroup fields={fields} name="accountHolderName">
          <Flex flexDirection="column" gap={Spacing.S2}>
            <Label id="legalRepresentative">{t('e_invoice.kyc.step.company_details.form.first_name.label')}</Label>
            <FormItemsGroup itemsInRow={2}>
              <FormItem
                name="accountHolderFirstName"
                render={({ field }) => (
                  <Input {...field} placeholder={t('e_invoice.kyc.step.company_details.form.first_name.placeholder')} />
                )}
              />
              <FormItem
                name="accountHolderLastName"
                render={({ field }) => (
                  <Input {...field} placeholder={t('e_invoice.kyc.step.company_details.form.last_name.placeholder')} />
                )}
              />
            </FormItemsGroup>
          </Flex>
        </ConditionalFormGroup>

        <ConditionalFormGroup fields={fields}>
          <FormItem
            label={companyRegistrationLabel}
            name="companyRegistrationNumber"
            render={({ field }) => (
              <Input
                {...field}
                type="number"
                value={field.value}
                placeholder={t('e_invoice.kyc.step.company_details.form.company_registration.placeholder')}
              />
            )}
          ></FormItem>
        </ConditionalFormGroup>

        <ConditionalFormGroup fields={fields} name="companyName">
          <FormItem
            label={t('e_invoice.kyc.step.company_details.form.company_name.label')}
            name="companyName"
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  placeholder={t('e_invoice.kyc.step.company_details.form.company_name.placeholder')}
                  suffix={<CompanyNameTooltipIcon />}
                />

                <Text variant="bodySM" colorVariant="secondary">
                  {t('e_invoice.kyc.step.company_details.form.company_name.hint')}
                </Text>
              </>
            )}
          ></FormItem>
        </ConditionalFormGroup>

        <ConditionalFormGroup fields={fields} name="address">
          <FormItem
            label={t('e_invoice.kyc.step.company_details.form.company_address.label')}
            name="companyAddress"
            render={({ field }) => (
              <Input
                {...field}
                placeholder={t('e_invoice.kyc.step.company_details.form.company_address.placeholder')}
              />
            )}
          ></FormItem>

          <FormItemsGroup itemsInRow={2} itemsTemplate={[2, 6]}>
            <FormItem
              name="companyZipCode"
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={t('e_invoice.kyc.step.company_details.form.company_address_zipcode.placeholder')}
                />
              )}
            />
            <FormItem
              name="companyCity"
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={t('e_invoice.kyc.step.company_details.form.company_address_city.placeholder')}
                />
              )}
            />
          </FormItemsGroup>
        </ConditionalFormGroup>
      </Flex>
    </>
  )
}
